import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, CheckCircle2, ExternalLink } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Textarea } from '~/components/ui/textarea'
import { cn } from '~/lib/utils'
import { AutoCaptureField } from './AutoCaptureField'
import { AttachmentField } from './AttachmentField'
import { useAutoCaptureBase } from '../hooks/useAutoCapture'
import { useScreenshotCapture } from '../hooks/useScreenshotCapture'
import { useReporterSubmit } from '../hooks/useReporterSubmit'
import { FOCUS_RING } from '../focus'
import type {
  AutoCapturePayload,
  ReporterCategory,
  ReporterFormValues,
  ReporterSeverity,
  ReporterSubmitResult,
  ReporterUser,
  RecentAction,
} from '../types'

interface ReporterModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  appName: string
  apiBaseUrl: string
  user?: ReporterUser | null
  recentActions?: RecentAction[]
  getAuthToken?: () => string | null
}

const SEVERITIES: ReporterSeverity[] = ['P0', 'P1', 'P2']
const CATEGORIES: ReporterCategory[] = ['UX', 'Visual', 'Logic', 'Performance', 'Other']

const EMPTY_FORM: ReporterFormValues = {
  title: '',
  description: '',
  severity: 'P2',
  category: 'Other',
  attachments: [],
}

export function ReporterModal({
  open,
  onOpenChange,
  appName,
  apiBaseUrl,
  user,
  recentActions,
  getAuthToken,
}: ReporterModalProps) {
  const [form, setForm] = useState<ReporterFormValues>(EMPTY_FORM)
  const [formError, setFormError] = useState<string | null>(null)
  const [successResult, setSuccessResult] = useState<ReporterSubmitResult | null>(null)
  const { route, ua, viewport } = useAutoCaptureBase()
  // appName is passed so a capture failure can be attributed to a host app in
  // Sentry — a spike on one app is the shape customer#156 had. See
  // useScreenshotCapture (customer#168).
  const { capture, isCapturing, last } = useScreenshotCapture(appName)
  const { submit, isSubmitting, error, reset } = useReporterSubmit({ apiBaseUrl, getAuthToken })

  // Trigger screenshot capture once when the modal opens.
  useEffect(() => {
    if (!open) return
    void capture()
  }, [open, capture])

  // Auto-close on success after 3 seconds.
  useEffect(() => {
    if (!successResult) return
    const t = setTimeout(() => {
      handleOpenChange(false)
    }, 3000)
    return () => clearTimeout(t)
    // handleOpenChange is stable (declared below as a callback).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successResult])

  /**
   * Centralizes modal open-state changes so we can reset transient form state
   * on close without using a setState-in-effect anti-pattern.
   */
  function handleOpenChange(next: boolean) {
    if (!next) {
      setForm(EMPTY_FORM)
      setFormError(null)
      setSuccessResult(null)
      reset()
    }
    onOpenChange(next)
  }

  const autoCapturePayload = useMemo<AutoCapturePayload | null>(() => {
    return {
      app: appName,
      route,
      browser: ua.browser,
      os: ua.os,
      device: ua.device,
      viewport,
      screenshot: last?.dataUrl ?? '',
      recentActions,
      timestamp: new Date().toISOString(),
      user: user ?? null,
    }
  }, [appName, route, ua, viewport, last?.dataUrl, recentActions, user])

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault()
    if (form.title.trim().length === 0) {
      setFormError('Title is required.')
      return
    }
    if (form.description.trim().length === 0) {
      setFormError('Description is required.')
      return
    }
    setFormError(null)

    if (!autoCapturePayload) return

    try {
      const result = await submit(
        {
          title: form.title.trim(),
          description: form.description.trim(),
          severity: form.severity,
          category: form.category,
          attachments: form.attachments,
        },
        autoCapturePayload,
      )
      setSuccessResult(result)
    } catch {
      // useReporterSubmit captures the error in `error`; modal stays open.
    }
  }

  // Cmd/Ctrl+Enter submits while the modal is open.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        void handleSubmit()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form, autoCapturePayload])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[520px]" data-testid="bug-reporter-modal">
        <DialogHeader>
          <DialogTitle>Report a bug</DialogTitle>
          <DialogDescription>
            Quick feedback on something broken in {appName}. Your context is captured automatically.
          </DialogDescription>
        </DialogHeader>

        {successResult ? (
          <SuccessState result={successResult} onClose={() => handleOpenChange(false)} />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="bug-title">
                Title <span className="text-red-600">*</span>
              </Label>
              <Input
                id="bug-title"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="One-line summary"
                maxLength={120}
                data-testid="bug-reporter-title"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="bug-description">
                Description <span className="text-red-600">*</span>
              </Label>
              <Textarea
                id="bug-description"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="What happened? What did you expect?"
                rows={4}
                data-testid="bug-reporter-description"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Severity</Label>
                <div className="flex flex-wrap gap-1.5">
                  {SEVERITIES.map(s => (
                    <PillToggle
                      key={s}
                      active={form.severity === s}
                      onClick={() => setForm(f => ({ ...f, severity: s }))}
                      label={s}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Category</Label>
                <div className="flex flex-wrap gap-1.5">
                  {CATEGORIES.map(c => (
                    <PillToggle
                      key={c}
                      active={form.category === c}
                      onClick={() => setForm(f => ({ ...f, category: c }))}
                      label={c}
                    />
                  ))}
                </div>
              </div>
            </div>

            <AutoCaptureField
              payload={autoCapturePayload}
              isCapturingScreenshot={isCapturing}
              screenshotError={last?.error ?? null}
            />

            <AttachmentField
              files={form.attachments}
              onChange={files => setForm(f => ({ ...f, attachments: files }))}
            />

            {formError && (
              <p className="flex items-center gap-1 text-xs text-red-600">
                <AlertCircle className="h-3.5 w-3.5" />
                {formError}
              </p>
            )}
            {error && !formError && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div className="flex-1">
                  <p>{error.message}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => void handleSubmit()}
                >
                  Try again
                </Button>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} data-testid="bug-reporter-submit">
                {isSubmitting ? 'Submitting…' : 'Submit'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

function PillToggle({
  active,
  onClick,
  label,
}: {
  active: boolean
  onClick: () => void
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
        FOCUS_RING,
        active
          ? 'border-slate-900 bg-slate-900 text-white'
          : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
      )}
    >
      {label}
    </button>
  )
}

function SuccessState({
  result,
  onClose,
}: {
  result: ReporterSubmitResult
  onClose: () => void
}) {
  return (
    <div className="flex flex-col items-center gap-3 py-4 text-center" data-testid="bug-reporter-success">
      <CheckCircle2 className="h-10 w-10 text-emerald-500" />
      <p className="text-base font-medium text-slate-900">Reported. Thanks!</p>
      {result.action === 'merged' && result.duplicateOf && (
        <p className="text-sm text-slate-600">
          Merged into existing issue{' '}
          <span className="font-mono text-xs">#{result.duplicateOf.slice(-6)}</span>
        </p>
      )}
      {result.dashboardUrl && (
        <a
          href={result.dashboardUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm font-medium text-slate-700 hover:underline"
        >
          View in dashboard
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
      <Button variant="outline" size="sm" onClick={onClose}>
        Close
      </Button>
    </div>
  )
}
